import { useEffect, useMemo, useState } from "react";

import {
  resolveMatchCountdownTargetUnix,
  computeRemainingSecondsUntilUnix,
  formatMatchCountdownHMS,
} from "@/utils/matchCountdown";

export function useMatchKickoffCountdown(primaryUnix, options = {}) {
  const { fallbackUnix = 0, isLive = false, enabled = true } = options;

  const targetUnix = useMemo(
    () => resolveMatchCountdownTargetUnix(primaryUnix, fallbackUnix),
    [primaryUnix, fallbackUnix],
  );

  const shouldTick = Boolean(enabled && targetUnix && !isLive);

  const [remainingSeconds, setRemainingSeconds] = useState(() =>
    shouldTick ? computeRemainingSecondsUntilUnix(targetUnix) : 0,
  );

  useEffect(() => {
    if (!shouldTick) {
      setRemainingSeconds(0);
      return undefined;
    }

    const tick = () =>
      setRemainingSeconds(computeRemainingSecondsUntilUnix(targetUnix));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [shouldTick, targetUnix]);

  const countdownDisplay = useMemo(() => {
    if (!enabled || !targetUnix || isLive) return "";
    return formatMatchCountdownHMS(remainingSeconds);
  }, [enabled, targetUnix, isLive, remainingSeconds]);

  return { targetUnix, remainingSeconds, countdownDisplay };
}
